import { RouteType } from "types/RouteType";
import RootStore from "../../RootStore";

/**
 * Manages authentication and permission checking for routes.
 * Follows Single Responsibility Principle by focusing only on permission management.
 */
export class PermissionManager {
    private rootStore: RootStore;
    private debugMode = false;

    constructor(rootStore: RootStore, debugMode: boolean = false) {
        this.rootStore = rootStore;
        this.debugMode = debugMode;
    }

    /**
     * Checks if the current user has permission to access a route
     * @param route - Route to check permissions for
     * @returns True if user has permission, false otherwise
     */
    checkRoutePermission(route: RouteType): boolean {
        // Check authentication requirement
        if (route.requiresAuth && !this.isUserAuthenticated()) {
            this.debugLog(`Route ${route.key} requires authentication but user is not authenticated`);
            return false;
        }

        // Check admin requirement
        if (route.requiresAdmin && !this.isUserAdmin()) {
            this.debugLog(`Route ${route.key} requires admin privileges but user is not admin`);
            return false;
        }

        // Check permission requirement
        if (route.requiresPermission && !this.hasRoutePermission(route)) {
            this.debugLog(`Route ${route.key} requires specific permission but user lacks access`);
            return false;
        }

        return true;
    }

    /**
     * Checks if the user is currently authenticated
     * @returns True if user is authenticated
     */
    isUserAuthenticated(): boolean {
        return this.rootStore.authStore?.isSignedIn || false;
    }

    /**
     * Checks if the user has admin privileges
     * @returns True if user is an admin
     */
    isUserAdmin(): boolean {
        const currentUser = this.rootStore.authStore?.currentUser;
        return currentUser?.adminUser || false;
    }

    /**
     * Checks if the user has permission for a specific route
     * @param route - Route to check permission for
     * @returns True if user has permission
     */
    hasRoutePermission(route: RouteType): boolean {
        const currentUser = this.rootStore.authStore?.currentUser;
        if (!currentUser) {
            return false;
        }

        // If no specific permission is required, allow access
        if (!route.requiresPermission) {
            return true;
        }

        // Check user permissions against route permission path
        const userPermissions = currentUser.permissions || [];
        const routePermissionPath = route.permissionPath || route.path;

        return this.checkPermissionAccess(userPermissions, routePermissionPath || '');
    }

    /**
     * Checks if user permissions allow access to a specific path
     * @param userPermissions - Array of user permission objects
     * @param permissionPath - Path to check access for
     * @returns True if user has access
     */
    private checkPermissionAccess(userPermissions: Record<string, unknown>[], permissionPath: string): boolean {
        if (!Array.isArray(userPermissions)) {
            return false;
        }

        // Check for exact match or wildcard permissions
        return userPermissions.some(permission => {
            const permLocation = String(permission.location || permission.path || '');

            // Exact match
            if (permLocation === permissionPath) {
                return true;
            }

            // Wildcard match (e.g., "/projects/*" matches "/projects/details")
            if (permLocation.endsWith('/*')) {
                const basePath = permLocation.slice(0, -2);
                return permissionPath.startsWith(basePath);
            }

            // Parent path match (e.g., "/projects" allows "/projects/details")
            if (permissionPath.startsWith(permLocation + '/')) {
                return true;
            }

            return false;
        });
    }

    /**
     * Gets routes that the current user has permission to access
     * @param routes - Array of routes to filter
     * @returns Array of routes the user can access
     */
    getAccessibleRoutes(routes: RouteType[]): RouteType[] {
        return routes.filter(route => this.checkRoutePermission(route));
    }

    /**
     * Handles authentication rerouting for protected routes
     * @param route - Route that requires authentication
     * @param params - Route parameters
     * @param queryParams - Query parameters
     * @returns Redirect information or null if no redirect needed
     */
    async handleAuthReroute(
        route: RouteType,
        params?: object | null,
        queryParams?: object
    ): Promise<{ route: string; params?: object; queryParams?: object } | null> {
        // If user is not authenticated and route requires auth, redirect to login
        if (route.requiresAuth && !this.isUserAuthenticated()) {
            return {
                route: 'LoginPage',
                queryParams: {
                    returnUrl: route.path,
                    ...queryParams
                }
            };
        }

        // If user is authenticated but lacks admin privileges for admin route
        if (route.requiresAdmin && this.isUserAuthenticated() && !this.isUserAdmin()) {
            return {
                route: 'Unauthorized',
                queryParams: {
                    reason: 'insufficient_privileges'
                }
            };
        }

        // If user lacks specific permissions
        if (route.requiresPermission && this.isUserAuthenticated() && !this.hasRoutePermission(route)) {
            return {
                route: 'Unauthorized',
                queryParams: {
                    reason: 'insufficient_permissions'
                }
            };
        }

        return null;
    }

    /**
     * Logs permission-related messages in debug mode
     * @param message - Message to log
     * @param data - Additional data to log
     */
    private debugLog(message: string, data?: unknown): void {
        if (this.debugMode) {
            console.log(`[PermissionManager] ${message}`, data);
        }
    }

    /**
     * Gets current user information for debugging
     * @returns Object with user authentication status
     */
    getCurrentUserInfo(): {
        isAuthenticated: boolean;
        isAdmin: boolean;
        permissions: Record<string, unknown>[];
    } {
        const currentUser = this.rootStore.authStore?.currentUser;
        return {
            isAuthenticated: this.isUserAuthenticated(),
            isAdmin: this.isUserAdmin(),
            permissions: currentUser?.permissions || []
        };
    }
}
